import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, get, post, type AdminUser } from '../api'
import { Button, Notice, Panel } from '../components/ui'
import { FactorBadge, FactorCancelled, SecondFactorProvider } from './SecondFactor'
import { useConfirmedAction, WordField } from './network/confirm'
import { Chip } from '../ui'
import { isoDate } from './network/format'

/*
 * A user's plan in the admin console (BILL-1 B2, docs/billing/design.md §9, §10.3):
 * the plan and where it comes from, every limit with its breakdown by layer, and
 * the admin's actions — a plan for a time (a gift of the top plan), ambassador
 * (every feature and the VIP badge), more trial days, a changed limit or feature,
 * a hold (no new streams, frozen, a blocked feature). Each action needs a reason
 * and the second factor; holds that refuse streams also a typed word. None of
 * them stops a live stream (unlike Block in Users).
 */

interface Step {
  layer: string
  op: string
  value: unknown
  result: unknown
  source: string
  reason?: string
  until?: string
  layer_id?: number
  ignored?: string
}

interface Card {
  account: { id: number; badges: string[]; created_at: string }
  entitlements: {
    plan_id: string
    plan_name: string
    plan_version: number
    plan_source: 'subscription' | 'free' | 'grant'
    plan_until?: string
    status: string
    access: 'full' | 'no_new_streams'
    access_reason?: string
    limits: Record<string, number | string | null>
    features: string[]
    breakdown: Record<string, Step[]>
    trial_ends_at?: string
  }
  subscriptions: { id: number; plan_id: string; status: string; trial_ends_at?: string; created_at: string }[]
  layers: {
    id: number
    layer: string
    key: string
    op: string
    value: unknown
    reason: string
    source_ref: string
    expires_at: string | null
    revoked_at: string | null
    created_at: string
  }[]
  holds: { id: number; kind: string; feature: string | null; reason: string; expires_at: string | null; lifted_at: string | null }[]
  enforced: string[]
}

const LIMIT_KEYS = [
  'destinations', 'concurrent_streams', 'stream_hours_month', 'captions_hours_month', 'transcode_outputs', 'transcode_hours_month',
  'translation_lang_hours_month', 'ingest_max', 'source_grace_max_s', 'history_days', 'transcript_days', 'seats',
]
const FEATURES = [
  'chat', 'chat_qa_highlights', 'captions', 'captions_quality', 'vertical_9_16', 'srt_ingest', 'no_watermark',
  'studio_app', 'bridge_app', 'stream_health', 'dedicated_ingest_region', 'priority_support', 'caption_translation',
]
const UNIT: Record<string, string> = {
  stream_hours_month: 'h / month', captions_hours_month: 'h / month', transcode_hours_month: 'h / month',
  translation_lang_hours_month: 'lang-h / month', history_days: 'days', transcript_days: 'days', source_grace_max_s: 's',
}

/** The system's plan badge (PlansAndLimits): monochrome, weight rises with the tier — never a state colour. */
const TIER: Record<string, string> = { free: 'free', creator: 'creator', pro: 'pro', business: 'business', ambassador: 'business' }

const shown = (v: unknown) => (v === null || v === undefined ? 'no limit' : String(v))
const day = (s?: string | null) => (s ? isoDate(s) : '')

export function AdminUserBilling() {
  return (
    <SecondFactorProvider>
      <BillingPage />
    </SecondFactorProvider>
  )
}

function BillingPage() {
  const { id } = useParams()
  const qc = useQueryClient()
  const key = ['admin-billing', id]
  const card = useQuery({ queryKey: key, queryFn: () => get<Card>(`/api/admin/users/${id}/billing`), retry: false })
  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => get<AdminUser[]>('/api/admin/users') })
  const email = users.data?.find((u) => String(u.id) === id)?.email ?? `user ${id}`
  const [msg, setMsg] = useState<{ kind: 'error' | 'info'; text: string } | null>(null)
  const action = useConfirmedAction()
  const [busy, setBusy] = useState(false)

  const send = async (path: string, body: object, done: string) => {
    setBusy(true)
    setMsg(null)
    try {
      const r = await action.run((c) => post(`/api/admin/users/${id}/billing${path}`, { ...body, ...c }))
      if (!r.ok) return false
      setMsg({ kind: 'info', text: done })
      await qc.invalidateQueries({ queryKey: key })
      return true
    } catch (e) {
      if (!(e instanceof FactorCancelled)) setMsg({ kind: 'error', text: (e as Error).message })
      return false
    } finally {
      setBusy(false)
    }
  }

  if (card.error) {
    const off = card.error instanceof ApiError && card.error.status === 409
    return <Notice>{off ? 'Billing is off on this center (BILLING_MODE=off): no plans to show.' : (card.error as Error).message}</Notice>
  }
  const c = card.data
  if (!c) return <p className="text-sm text-muted">Loading…</p>
  const e = c.entitlements
  const vip = c.account.badges.includes('vip')
  return (
    <div className="space-y-6" data-testid="user-billing">
      <p className="text-sm">
        <Link to="/admin/users" className="text-text-soft underline underline-offset-2">Users</Link> / {email}
      </p>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <Panel title={
        <span data-testid="billing-plan" className="flex items-center gap-2">
          {/* a tier plan is its badge; others (trial, custom) stay words */}
          {TIER[e.plan_id] ? <span className={`sx-plan ${TIER[e.plan_id]} !h-6 !px-2 !text-xs`}>{e.plan_name}</span> : e.plan_name}
          {vip && <span className="sx-plan business" data-testid="billing-vip">VIP</span>}
        </span>
      } aside={<FactorBadge />}>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">Comes from</dt>
          <dd data-testid="billing-source">
            {e.plan_source === 'grant' ? `given by an admin${e.plan_until ? ` until ${day(e.plan_until)}` : ', no end date'}` : e.plan_source === 'free' ? 'free — the floor of the service' : `subscription (${e.status || 'none'})`}
          </dd>
          {e.trial_ends_at && (<><dt className="text-muted">Trial ends</dt><dd>{day(e.trial_ends_at)}</dd></>)}
          <dt className="text-muted">New streams</dt>
          <dd data-testid="billing-access">{e.access === 'full' ? 'allowed' : `refused: ${e.access_reason}`}</dd>
          <dt className="text-muted">Checks that deny</dt>
          <dd>{c.enforced.length ? c.enforced.join(', ') : 'none — every check only observes (ENTITLEMENTS_ENFORCE)'}</dd>
          <dt className="text-muted">Features</dt>
          <dd className="font-mono text-xs">{e.features.join(' · ')}</dd>
        </dl>
      </Panel>

      <Panel title="Limits and why">
        {/* MetricsTable, compact (console): label heads, the value in mono on the right, "no limit" muted */}
        <div className="-m-4 overflow-x-auto">
          <table className="sx-table compact min-w-[640px]">
            <thead>
              <tr><th scope="col">Limit</th><th scope="col" className="num">Value</th><th scope="col">Layers</th></tr>
            </thead>
            <tbody>
              {LIMIT_KEYS.map((k) => (
                <tr key={k} data-testid={`limit-${k}`}>
                  <td className="font-mono text-xs">{k}</td>
                  <td className={`num tabular ${e.limits[k] === null || e.limits[k] === undefined ? 'dim' : ''}`}>{shown(e.limits[k])}{e.limits[k] !== null && UNIT[k] ? <span className="ml-1 font-sans text-xs text-muted">{UNIT[k]}</span> : null}</td>
                  <td className="text-xs whitespace-normal text-text-soft">
                    {(e.breakdown[k] ?? []).map((s, i) => (
                      <span key={i} className={`mr-3 inline-block ${s.ignored ? 'line-through' : ''}`} title={[s.reason, s.ignored].filter(Boolean).join(' — ')}>
                        {s.layer} {s.op} {shown(s.value)}{s.until ? ` until ${day(s.until)}` : ''} → {shown(s.result)}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        <GrantPlan busy={busy} onSend={(b) => send('/plan', b, `${b.plan} given`)} />
        <Ambassador on={vip} busy={busy} onSend={(b) => send('/ambassador', b, b.on ? 'Ambassador on' : 'Ambassador off')} />
        <TrialDays busy={busy} onSend={(b) => send('/trial', b, `Trial extended by ${b.days} days`)} />
        <ChangeLimit busy={busy} onSend={(b) => send('/layers', b, 'Change added')} />
        <AddHold busy={busy} onSend={(b) => send('/holds', b, 'Hold added')} />
      </div>
      {action.asked && (
        <Panel title="The center asks for a word">
          <div className="space-y-3">
            <WordField word={action.asked.word} value={action.word} onChange={action.setWord} testId="billing-word" />
            <p className="text-xs text-muted">Press the action again once the word matches.</p>
          </div>
        </Panel>
      )}

      <Panel title="Changes in force">
        <ul className="divide-y divide-line text-sm">
          {c.layers.filter((l) => !l.revoked_at && (!l.expires_at || Date.parse(l.expires_at) > Date.now())).map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-3 py-2" data-testid={`layer-${l.id}`}>
              <span className="font-mono text-xs">{l.key} {l.op} {JSON.stringify(l.value)}</span>
              <span className="text-muted">{l.expires_at ? `until ${day(l.expires_at)}` : 'no end date'}</span>
              <span className="flex-1 text-text-soft">{l.reason}</span>
              <Revoke label="Revoke" busy={busy} onSend={(reason) => send(`/layers/${l.id}/revoke`, { reason }, 'Revoked')} />
            </li>
          ))}
          {c.holds.filter((h) => !h.lifted_at && (!h.expires_at || Date.parse(h.expires_at) > Date.now())).map((h) => (
            <li key={`h${h.id}`} className="flex flex-wrap items-center gap-3 py-2" data-testid={`hold-${h.id}`}>
              <Chip state="warn" size="sm">{h.kind}{h.feature ? `: ${h.feature}` : ''}</Chip>
              <span className="text-muted">{h.expires_at ? `until ${day(h.expires_at)}` : 'until lifted'}</span>
              <span className="flex-1 text-text-soft">{h.reason}</span>
              <Revoke label="Lift" busy={busy} onSend={(reason) => send(`/holds/${h.id}/lift`, { reason }, 'Lifted')} />
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  )
}

/** The system's fields (Fields): line-strong edge, focus ring; selects as .sx-select. */
const input = 'sx-input sans w-full'
const select = 'sx-select w-full'

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="sx-field">
      <span className="lbl">{label}</span>
      {children}
    </label>
  )
}

function Reason({ value, onChange, testId }: { value: string; onChange: (v: string) => void; testId: string }) {
  return (
    <Labeled label="Reason (10+ characters, goes to the log)">
      <input className={input} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </Labeled>
  )
}

type Send<T> = (body: T) => Promise<boolean>

function GrantPlan({ busy, onSend }: { busy: boolean; onSend: Send<{ plan: string; days: number; reason: string }> }) {
  const [plan, setPlan] = useState('business')
  const [days, setDays] = useState(30)
  const [reason, setReason] = useState('')
  return (
    <Panel title="Give a plan for a time">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Plan">
            <select className={select} value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="grant-plan">
              <option value="business">Business (top)</option><option value="pro">Pro</option><option value="creator">Creator</option>
            </select>
          </Labeled>
          <Labeled label="For">
            <select className={select} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="grant-days">
              <option value={30}>1 month</option><option value={90}>3 months</option><option value={180}>6 months</option><option value={365}>1 year</option>
            </select>
          </Labeled>
        </div>
        <Reason value={reason} onChange={setReason} testId="grant-reason" />
        <p className="text-xs text-muted">The paid subscription is not touched; the higher plan wins. A live stream goes on.</p>
        <Button disabled={busy || reason.trim().length < 10} data-testid="grant-send"
          onClick={async () => (await onSend({ plan, days, reason })) && setReason('')}>Give {plan}</Button>
      </div>
    </Panel>
  )
}

function Ambassador({ on, busy, onSend }: { on: boolean; busy: boolean; onSend: Send<{ on: boolean; reason: string }> }) {
  const [reason, setReason] = useState('')
  return (
    <Panel title="Ambassador">
      <div className="space-y-3">
        <p className="text-sm text-text-soft">Every feature, the top plan's limits and a VIP mark in the console and the cabinet. No end date; review it yearly.</p>
        <Reason value={reason} onChange={setReason} testId="ambassador-reason" />
        <Button variant={on ? 'danger' : 'plain'} disabled={busy || reason.trim().length < 10} data-testid="ambassador-send"
          onClick={async () => (await onSend({ on: !on, reason })) && setReason('')}>{on ? 'Remove ambassador' : 'Make ambassador'}</Button>
      </div>
    </Panel>
  )
}

function TrialDays({ busy, onSend }: { busy: boolean; onSend: Send<{ days: number; reason: string }> }) {
  const [days, setDays] = useState(7)
  const [reason, setReason] = useState('')
  return (
    <Panel title="More trial days">
      <div className="space-y-3">
        <Labeled label="Days (at most 14 in total per account)">
          <input type="number" min={1} max={14} className={input} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="trial-days" />
        </Labeled>
        <Reason value={reason} onChange={setReason} testId="trial-reason" />
        <Button disabled={busy || reason.trim().length < 10 || days < 1 || days > 14} data-testid="trial-send"
          onClick={async () => (await onSend({ days, reason })) && setReason('')}>Extend trial</Button>
      </div>
    </Panel>
  )
}

function ChangeLimit({ busy, onSend }: { busy: boolean; onSend: Send<{ key: string; op: string; value: unknown; days?: number; reason: string; confirm?: string }> }) {
  const [key, setKey] = useState('destinations')
  const [op, setOp] = useState('add')
  const [value, setValue] = useState('5')
  const [days, setDays] = useState(30)
  const [reason, setReason] = useState('')
  const feature = FEATURES.includes(key)
  const ops = feature ? ['grant', 'deny'] : key === 'ingest_max' || /_days$|_s$/.test(key) ? ['set', 'max', 'min'] : ['add', 'set', 'max', 'min']
  const curOp = ops.includes(op) ? op : (ops[0] ?? 'set')
  const parsed = value.trim() === '' ? null : /^\d+$/.test(value.trim()) ? Number(value.trim()) : value.trim()
  return (
    <Panel title="Change a limit or a feature">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Labeled label="What">
            <select className={select} value={key} onChange={(e) => setKey(e.target.value)} data-testid="layer-key">
              <optgroup label="Limits">{LIMIT_KEYS.map((k) => <option key={k}>{k}</option>)}</optgroup>
              <optgroup label="Features">{FEATURES.map((k) => <option key={k}>{k}</option>)}</optgroup>
            </select>
          </Labeled>
          <Labeled label="How">
            <select className={select} value={curOp} onChange={(e) => setOp(e.target.value)} data-testid="layer-op">
              {ops.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Labeled>
          {!feature && (
            <Labeled label="Value (empty = no limit)">
              <input className={input} value={value} onChange={(e) => setValue(e.target.value)} data-testid="layer-value" />
            </Labeled>
          )}
        </div>
        <Labeled label="For (days; lowering may be open-ended: 0)">
          <input type="number" min={0} max={366} className={input} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="layer-days" />
        </Labeled>
        <Reason value={reason} onChange={setReason} testId="layer-reason" />
        <Button disabled={busy || reason.trim().length < 10} data-testid="layer-send"
          onClick={async () => (await onSend({ key, op: curOp, value: feature ? true : parsed, ...(days > 0 ? { days } : {}), reason })) && setReason('')}>Apply</Button>
      </div>
    </Panel>
  )
}

function AddHold({ busy, onSend }: { busy: boolean; onSend: Send<{ kind: string; feature?: string; days?: number; reason: string }> }) {
  const [kind, setKind] = useState('frozen')
  const [feature, setFeature] = useState('captions')
  const [days, setDays] = useState(0)
  const [reason, setReason] = useState('')
  return (
    <Panel title="Hold">
      <div className="space-y-3">
        <p className="text-sm text-text-soft">Refuses new streams or a feature. A live stream goes on; to stop it use Block in Users.</p>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Kind">
            <select className={select} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="hold-kind">
              <option value="frozen">Freeze the account</option><option value="streams_blocked">No new streams</option>
              <option value="feature_blocked">Block a feature</option><option value="paused">Pause the plan</option>
            </select>
          </Labeled>
          {kind === 'feature_blocked' ? (
            <Labeled label="Feature">
              <select className={select} value={feature} onChange={(e) => setFeature(e.target.value)} data-testid="hold-feature">
                {FEATURES.map((f) => <option key={f}>{f}</option>)}
              </select>
            </Labeled>
          ) : (
            <Labeled label="For (days, 0 = until lifted)">
              <input type="number" min={0} max={366} className={input} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="hold-days" />
            </Labeled>
          )}
        </div>
        <Reason value={reason} onChange={setReason} testId="hold-reason" />
        <Button variant="danger" disabled={busy || reason.trim().length < 10} data-testid="hold-send"
          onClick={async () => (await onSend({ kind, ...(kind === 'feature_blocked' ? { feature } : days > 0 ? { days } : {}), reason })) && setReason('')}>Add hold</Button>
      </div>
    </Panel>
  )
}

function Revoke({ label, busy, onSend }: { label: string; busy: boolean; onSend: (reason: string) => Promise<boolean> }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  if (!open) return <Button variant="quiet" onClick={() => setOpen(true)}>{label}</Button>
  return (
    <span className="flex items-center gap-2">
      <input aria-label="Reason" placeholder="Reason (10+ characters)" className="sx-input sans w-56" value={reason} onChange={(e) => setReason(e.target.value)} />
      <Button variant="danger" disabled={busy || reason.trim().length < 10} onClick={async () => (await onSend(reason)) && setOpen(false)}>{label}</Button>
      <Button variant="quiet" onClick={() => setOpen(false)}>Cancel</Button>
    </span>
  )
}
